import type { ModelError_Item } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useEffect } from 'react';
import type { UseFormSetError } from 'react-hook-form';

import useDispatchNumberLabel from '~hooks/useDispatchNumberLabel';
import type { NewOrderFormSchemaInterface } from '~pages/Sales/Orders/newOrderFormSchema';

import type { OrderFormV3FieldValues } from '../OrderFormV3Context';

/**
 * Schema keys whose backend uniqueness 422 — "<field> has already been taken" —
 * we rephrase so the leading field token matches the form's own label casing
 * (the BE lower-cases it, e.g. "external ID has already been taken"). The label
 * KEYS are exactly the ones the legacy Order Customer Details card renders
 * (`NewOrderForm/OrderCustomerDetailsV2.tsx`), so the inline error always reads
 * in the casing the user sees on the field. Keyed by schema key — the loop
 * below has already resolved the BE field name to one.
 *
 * Fields absent here keep the raw server message — this is a deliberate
 * pass-through: the frontend cannot know every uniqueness-constrained field a
 * priori, and the `orderFieldMetadata` label is unusable for this — it is the
 * table-abbreviated form (`order.form.external_id` → "Ord. Ext. ID", not
 * "External ID").
 */
const ALREADY_TAKEN_FIELD_LABEL_KEYS: Partial<
  Record<keyof NewOrderFormSchemaInterface, string>
> = {
  externalId: 'form_fields.external_id',
  name: 'order.form.order_name',
  contractNumber: 'order.form.contract_number',
  dispatchNumber: 'order.form.dispatch_number_full',
  poJobNumber: 'order.form.order_po',
  zone: 'order.form.order_zone',
};

// Backend phrasings seen for the uniqueness 422: "<field> has already been
// taken" and the bare "has already been taken". Tolerant on purpose, mirroring
// the add-on detector in processAddOnAssignmentsForOrder.ts.
const ALREADY_TAKEN_MESSAGE_PATTERN = /already\s+(been\s+)?taken/i;

/**
 * Rephrase a uniqueness 422 to "<field label> has already been taken" using the
 * form's own label so the field token matches its on-screen casing; every other
 * message passes through unchanged.
 */
function rephraseAlreadyTakenMessage(
  matched: keyof NewOrderFormSchemaInterface,
  message: string,
  singular: (fallbackKey: string) => string,
) {
  const labelKey = ALREADY_TAKEN_FIELD_LABEL_KEYS[matched];
  if (!labelKey || !ALREADY_TAKEN_MESSAGE_PATTERN.test(message)) {
    return message;
  }
  const field = matched === 'dispatchNumber' ? singular(labelKey) : t(labelKey);

  // React escapes at render; i18next's global escapeValue would turn the "/" in
  // "Customer Ref./PO" into "&#x2F;".
  return t('error_messages.field_already_taken', {
    field,
    interpolation: { escapeValue: false },
  });
}

/**
 * Triggers: the Provider's `errors` prop — OrderForm.tsx's `asyncErrors`
 *           state, set from a save 422's `ModelError_Item[]` body (the
 *           ProjectDialog.tsx catch pattern).
 * Writes:   `setError(<matched key>, { type: 'manual', message })` for every
 *           item whose BE `field` maps to a schema key — `type: 'manual'` is
 *           the channel the form widgets render as inline helper text (see
 *           TextFormField.tsx's `hasAsyncError` prop). A uniqueness 422
 *           ("<field> has already been taken") on a known identifier field is
 *           rephrased so the field token matches the form's own label casing
 *           (`rephraseAlreadyTakenMessage`); every other message passes through
 *           verbatim. Unmapped items (`model: 'order', field: 'base'`, nested
 *           waypoint paths, …) fall through to an error toast so the user
 *           still gets feedback.
 * Guards:   no-op while the list is empty; field names try a direct match
 *           first (schema keys that are already BE-shaped), then snake→camel
 *           for the conventional Rails names (`truck_count` → `truckCount`).
 * Legacy:   none — the legacy save path surfaces 422s only as a generic
 *           failed footer status (a known gap in the legacy implementation);
 *           this module ports the shipped project-v2 standard
 *           (ProjectFormV2Context.tsx inline-error mapping) as its own
 *           dedicated effect module.
 * Test:     __tests__/OrderFormV3.test.tsx (the inline 422 error suite).
 */
export function useAsyncErrorMapping({
  errors,
  schemaFieldKeys,
  setError,
  pushErrorAlert,
}: {
  errors: ModelError_Item[] | undefined;
  schemaFieldKeys: ReadonlySet<keyof NewOrderFormSchemaInterface>;
  setError: UseFormSetError<OrderFormV3FieldValues>;
  pushErrorAlert: (message: string) => void;
}): void {
  const { singular } = useDispatchNumberLabel();

  useEffect(() => {
    if (!errors || errors.length === 0) {
      return;
    }
    for (const err of errors) {
      const direct = err.field;
      const camel = err.field.replace(/_([a-z0-9])/g, (_, ch: string) =>
        ch.toUpperCase(),
      );
      const directKey = direct as keyof NewOrderFormSchemaInterface;
      const camelKey = camel as keyof NewOrderFormSchemaInterface;
      const matched: keyof NewOrderFormSchemaInterface | null = schemaFieldKeys.has(
        directKey,
      )
        ? directKey
        : schemaFieldKeys.has(camelKey)
          ? camelKey
          : null;
      if (matched) {
        setError(matched, {
          type: 'manual',
          message: rephraseAlreadyTakenMessage(matched, err.message, singular),
        });
      } else {
        pushErrorAlert(err.message);
      }
    }
    // setError is RHF-stable; schemaFieldKeys/pushErrorAlert are memoized in
    // the Provider. Re-run only when the `errors` identity changes (one
    // mapping pass per failed save).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors]);
}
